// ═══════════════════════════════════════════════════════════════════
// consigneCore.js — Cœur logique du module Consigne (bagages + matériel)
//
// Fonctions PURES uniquement (aucun React, aucun réseau, aucun DOM) :
// testables isolément et partagées par l'UI publique (ConsigneClientUI),
// les écrans staff (dépôt, retrait, retour) et — plus tard — les
// agrégations du dashboard et l'adaptateur de facturation.
//
// Périmètre de CETTE tranche (1re session de code du module) :
//   - Tarif consigne bagages (grille fine 5€/3€ + dégressif groupe +
//     préférentiel logement géré).
//   - Estimation matériel (réservation) + choix du prestataire de caution.
//   - Prix DÉFINITIF au retour du matériel "jour dégressif" (poussette :
//     jour de base + jours supp. + exception 24h pile).
//   - Nuitées (lit bébé).
//   Les agrégations dashboard (caLignes) et buildLignesConsigne (facture)
//   arriveront dans une tranche ultérieure de ce même fichier.
//
// Décisions de conception (SPEC_consigne_v2, CONCEPTION_consigne_v1) —
// comportements calqués VERBATIM sur la démo validée avec Claire
// (demo_consigne_materiel.html : calcC1 / calc2 / calcMulti / calcRetour) :
//
//  - PRÉFÉRENTIEL : remplace le SEUL tarif "grand" (5€ → 3€) ; le tarif
//    "petit" reste inchangé (déjà 3€). Il est DÉRIVÉ du drapeau du
//    logement géré (fiche appartement), jamais d'un toggle par dépôt.
//  - DÉGRESSIF GROUPE : appliqué seulement s'il est RÉELLEMENT plus
//    avantageux que la grille fine (comparaison, jamais imposé). Seuil
//    par défaut 11 bagages ("au-delà de 10").
//  - ESTIMATION vs DÉFINITIF : à la réservation, la poussette est estimée
//    au tarif jour simple (indicatif) ; le prix RÉEL se calcule au retour
//    sur l'heure réelle (bouton "Retour"). Les deux fonctions sont donc
//    distinctes et peuvent diverger — c'est voulu.
//  - Le "jour dégressif" (base + jours supp. + 24h pile) est le mécanisme
//    de la POUSSETTE. Chaise haute / fauteuil / garage = tarif jour plat,
//    réglé à la réservation, PAS de recalcul au retour (SPEC §1.2.3).
//    Lit bébé = nuitées calendaires classiques (SPEC §1.2.2).
//  - Tous les tarifs proviennent de consigneSettings7 / consigneCatalogue7
//    (paramétrables) ; les valeurs ci-dessous ne sont que des défauts.
// ═══════════════════════════════════════════════════════════════════

// ---- Défauts (repris de la démo, surchargés par consigneSettings7) ----
export const TARIFS_CONSIGNE_DEFAUT = Object.freeze({
  grand: 5,          // valise, sac de voyage, sac de randonnée
  petit: 3,          // sac à dos, tote-bag, petit sac à main
  preferentiel: 3,   // remplace "grand" pour un locataire d'un bien géré
  groupe: 4,         // tarif dégressif À PARTIR du seuil (seuil inclus)
  groupeSeuil: 10,   // "à partir de 10 bagages" (décision Claire, v179)
});

// Seuil de bascule du prestataire de caution matériel (SPEC §4) :
//  ≤ 7 jours → Stripe (autorisation manuelle 7 j, fonctionne déjà) ;
//  > 7 jours → Swikly ou espèces en boutique.
export const SEUIL_CAUTION_JOURS = 7;

// ---- Petits utilitaires ----

/** Arrondi monétaire au centime (garde-fou flottant). */
/**
 * Réessaie `fn` jusqu'à `tentatives` fois (délai fixe `delaiMs` entre deux
 * essais) avant d'abandonner — décision Claire (v217) : la tablette du
 * comptoir a du wifi, un échec d'écriture est presque toujours un aléa
 * Supabase de quelques secondes, jamais une vraie coupure. Le staff ne doit
 * jamais avoir à recliquer devant un client qui attend — la fonction
 * absorbe ça en coulisse, invisible si tout va bien au 2ᵉ ou 3ᵉ essai.
 * Relance la dernière erreur si tous les essais échouent (l'appelant décide
 * alors du message affiché). PURE dans sa logique de retry — `fn` est le
 * seul effet de bord, injecté par l'appelant (testable en lui passant un
 * mock qui échoue N fois puis réussit).
 * @param {() => Promise<T>} fn
 * @param {{tentatives?: number, delaiMs?: number}} opts
 * @returns {Promise<T>}
 */
export async function avecRetry(fn, opts = {}) {
  const tentatives = Math.max(1, Number(opts.tentatives) || 3);
  const delaiMs = Math.max(0, Number(opts.delaiMs) || 600);
  let derniereErreur;
  for (let i = 0; i < tentatives; i++) {
    try {
      return await fn();
    } catch (e) {
      derniereErreur = e;
      if (i < tentatives - 1) await new Promise((r) => setTimeout(r, delaiMs));
    }
  }
  throw derniereErreur;
}

export function eur(x) {
  return Math.round((Number(x) || 0) * 100) / 100;
}

/**
 * Parse une valeur date/heure en objet Date.
 * Accepte un Date déjà construit ou une chaîne ISO. Les chaînes locales
 * "YYYY-MM-DDTHH:mm(:ss)" sans fuseau sont interprétées en heure LOCALE
 * (heure murale du comptoir), cohérent avec la démo et la saisie terrain.
 * Renvoie null si la valeur est inexploitable.
 */
export function parseDT(v) {
  if (v == null || v === '') return null;
  const d = v instanceof Date ? v : new Date(v);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * Nombre de jours d'une DURÉE (estimation réservation) = ceil de la durée
 * en jours, minimum 1. Calque exact de nbJoursDT() de la démo.
 * Ex. 8h → 1 ; 24h pile → 1 ; 26h → 2.
 */
export function nbJoursDuree(depot, retrait) {
  const a = parseDT(depot), b = parseDT(retrait);
  if (!a || !b) return 1;
  const diff = (b - a) / 86400000;
  return Math.max(1, Math.ceil(diff) || 1);
}

/**
 * Nombre de jours CALENDAIRES franchis entre deux instants (l'heure est
 * ignorée : on compare les dates seules, en composantes locales).
 * Calque exact de calendarDayDiff() de la démo.
 * Ex. J0 11h → J0 19h = 0 ; J0 11h → J1 10h = 1 ; J0 → J2 = 2.
 */
export function nbJoursCalendaires(a, b) {
  const d1 = parseDT(a), d2 = parseDT(b);
  if (!d1 || !d2) return 0;
  const j1 = new Date(d1.getFullYear(), d1.getMonth(), d1.getDate());
  const j2 = new Date(d2.getFullYear(), d2.getMonth(), d2.getDate());
  return Math.round((j2 - j1) / 86400000);
}

/** Durée réelle en heures entre deux instants (peut être fractionnaire). */
export function dureeHeures(a, b) {
  const d1 = parseDT(a), d2 = parseDT(b);
  if (!d1 || !d2) return 0;
  return (d2 - d1) / 3600000;
}

/**
 * Nombre de jours FACTURABLES pour la consigne BAGAGES = jours civils
 * entamés (minuit à minuit), minimum 1. Dédiée aux bagages uniquement —
 * la poussette/matériel garde son propre mécanisme dégressif
 * (prixDegressif, "24h pile") et n'utilise jamais cette fonction.
 * Contrairement à nbJoursDuree (tranches de 24h glissantes, qui masque
 * silencieusement un jour facturable quand l'écart réel tombe sous 24h
 * même en cas de franchissement de minuit), celle-ci compte chaque jour
 * civil entamé, conformément à la grille "par bagage et par jour". Reste
 * la règle de base même quand `supplement24hActif` est actif (voir
 * nbJoursEtSupplementBagages ci-dessous) — celui-ci ne fait qu'ajouter un
 * supplément ciblé sur un cas précis, il ne remplace jamais ce calcul.
 * Ex. dépôt 25/08 20h → retrait 26/08 18h (< 24h réelles) = 2 jours.
 * Ex. dépôt 26/08 23h59 → retrait 27/08 00h01 (2 min réelles) = 2 jours.
 * Ex. dépôt/retrait même jour, n'importe quelle heure = 1 jour (minimum).
 */
export function nbJoursFacturablesBagages(depot, retrait) {
  return Math.max(1, nbJoursCalendaires(depot, retrait) + 1);
}

/**
 * « Supplément nuit courte » (v191, décision Claire) — trois conditions
 * CUMULATIVES, toutes nécessaires :
 *   (1) le retrait tombe le jour civil qui suit IMMÉDIATEMENT le dépôt
 *       (un seul minuit franchi — un dépôt à 1h retiré le lendemain matin
 *       n'est PAS concerné si la durée réelle dépasse 24h, voir (2)) ;
 *   (2) la durée réelle entre dépôt et retrait ne dépasse pas 24h pleines ;
 *   (3) l'heure de retrait ne dépasse pas un seuil paramétrable (Réglages
 *       → `supplement24hSeuilHeure`, défaut 11h).
 * Cible le dépôt tardif / retrait matinal du lendemain (ex. 22h → 7h),
 * aujourd'hui facturé 2 jours civils entamés malgré une nuit courte.
 * N'affecte JAMAIS le matériel (poussette et consorts gardent leur propre
 * mécanisme "24h pile" par article, `exception24hPile`/`tarif24hPile` au
 * catalogue — sans rapport avec cette fonction). PURE.
 * @returns {boolean}
 */
export function supplementNuitApplicable(depot, retrait, seuilHeure = 11) {
  const d1 = parseDT(depot), d2 = parseDT(retrait);
  if (!d1 || !d2) return false;
  const dureeMs = d2.getTime() - d1.getTime();
  if (dureeMs <= 0 || dureeMs > 24 * 3600000) return false;
  const jourDepot = new Date(d1.getFullYear(), d1.getMonth(), d1.getDate());
  const jourRetrait = new Date(d2.getFullYear(), d2.getMonth(), d2.getDate());
  const diffJours = Math.round((jourRetrait - jourDepot) / 86400000);
  if (diffJours !== 1) return false;
  const seuil = Number(seuilHeure);
  const s = Number.isFinite(seuil) ? seuil : 11;
  const heureRetrait = d2.getHours() + d2.getMinutes() / 60;
  return heureRetrait <= s;
}

/**
 * Nombre de jours facturables BAGAGES + éventuel supplément nuit courte.
 * Combine nbJoursFacturablesBagages et supplementNuitApplicable, et lit
 * les 3 réglages (`supplement24hActif`/`Montant`/`SeuilHeure`) en un seul
 * endroit — les 3 points d'appel (UI publique, comptoir, Edge Function
 * Stripe) délèguent tous ici plutôt que de dupliquer la condition. Quand
 * le supplément s'applique, il REMPLACE le 2ᵉ jour (1 jour + supplément),
 * jamais un ajout aux 2 jours (décision Claire, v191). PURE.
 * @returns {{ jours:number, supplementNuitApplique:boolean, supplementNuitMontant:number }}
 */
export function nbJoursEtSupplementBagages(depot, retrait, settings = {}) {
  const actif = !!(settings && settings.supplement24hActif);
  const applicable = actif && supplementNuitApplicable(depot, retrait, settings.supplement24hSeuilHeure);
  const jours = applicable ? 1 : nbJoursFacturablesBagages(depot, retrait);
  const montant = applicable ? (Number(settings.supplement24hMontant) || 0) : 0;
  return { jours, supplementNuitApplique: applicable, supplementNuitMontant: montant };
}

// ─────────────────────────────────────────────────────────────────────
// 1. CONSIGNE BAGAGES
// ─────────────────────────────────────────────────────────────────────

/**
 * Prix d'une consigne bagages. Calque de calcC1 / calc2 de la démo.
 *
 * @param {object} p
 * @param {number} p.nbGrands   Nombre de bagages "grand".
 * @param {number} p.nbPetits   Nombre de bagages "petit".
 * @param {number} p.jours      Durée en jours (voir nbJoursDuree).
 * @param {boolean} p.preferentiel  true si logement géré éligible → tarif
 *                                   préférentiel sur les "grands".
 * @param {object} [p.tarifs]   Grille (défaut TARIFS_CONSIGNE_DEFAUT).
 * @returns {{ total:number, lignes:{label:string,montant:number}[],
 *             groupeApplique:boolean, formule:'grille'|'groupe_grands'|'groupe_tous' }}
 *
 * TARIF GROUPE (révision v179, décision Claire) — le moteur évalue TROIS
 * formules et retient TOUJOURS la moins chère pour le client :
 *   - `grille`        : grille fine (chaque bagage à son tarif).
 *   - `groupe_grands` : les GRANDS passent au tarif groupe, les petits gardent
 *                       le leur. C'est la règle métier énoncée par Claire
 *                       (« à partir de 10 bagages, les grands passent à 4€ »).
 *   - `groupe_tous`   : tous les bagages au tarif groupe. Conservé pour le cas
 *                       où le tarif groupe descendrait un jour SOUS le tarif
 *                       petit — sinon `groupe_grands` le domine toujours.
 * Les deux formules groupe ne sont candidates qu'à partir du seuil (INCLUS).
 * Avant v179 seule `groupe_tous` existait : elle appliquait le tarif groupe
 * aux petits aussi, donc les renchérissait (4€ > 3€), et la comparaison la
 * bloquait dès qu'il y avait un petit — le dégressif ne sortait jamais.
 */
export function prixConsigne({ nbGrands = 0, nbPetits = 0, jours = 1,
                              preferentiel = false, tarifs, supplementNuit = 0 } = {}) {
  const T = { ...TARIFS_CONSIGNE_DEFAUT, ...(tarifs || {}) };
  const grand = Math.max(0, Math.floor(nbGrands) || 0);
  const petit = Math.max(0, Math.floor(nbPetits) || 0);
  const j = Math.max(1, Math.floor(jours) || 1);
  const nb = grand + petit;

  const tGrand = preferentiel ? T.preferentiel : T.grand;
  const tPetit = Number(T.petit) || 0;
  const tGroupe = Number(T.groupe) || 0;
  // Garde-fous : un seuil absent/nul ferait sortir le dégressif dès 0 bagage,
  // et un tarif groupe nul/négatif rendrait la consigne gratuite en silence.
  const seuil = Number(T.groupeSeuil) > 0
    ? Math.floor(Number(T.groupeSeuil))
    : TARIFS_CONSIGNE_DEFAUT.groupeSeuil;
  const groupeCandidat = tGroupe > 0 && nb >= seuil;

  const ligneGrands = (tarif, suffixe) => ({
    label: `${grand} grand(s) × ${tarif}€${suffixe} × ${j}j`,
    montant: eur(grand * tarif * j),
  });
  const lignePetits = () => ({
    label: `${petit} petit(s) × ${tPetit}€ × ${j}j`,
    montant: eur(petit * tPetit * j),
  });

  const formules = [{
    cle: 'grille',
    total: grand * tGrand * j + petit * tPetit * j,
    lignes: () => [
      ...(grand ? [ligneGrands(tGrand, preferentiel ? ' (préférentiel)' : '')] : []),
      ...(petit ? [lignePetits()] : []),
    ],
  }];

  if (groupeCandidat) {
    formules.push({
      cle: 'groupe_grands',
      total: grand * tGroupe * j + petit * tPetit * j,
      lignes: () => [
        ...(grand ? [ligneGrands(tGroupe, ' (tarif groupe)')] : []),
        ...(petit ? [lignePetits()] : []),
      ],
    });
    formules.push({
      cle: 'groupe_tous',
      total: nb * tGroupe * j,
      lignes: () => [{
        label: `${nb} bagages (tarif groupe ${tGroupe}€) × ${j}j`,
        montant: eur(nb * tGroupe * j),
      }],
    });
  }

  // Moins-disant strict : à égalité, la formule la plus haute dans la liste
  // gagne — donc `grille` par défaut, pour ne pas annoncer un « tarif groupe »
  // qui ne ferait économiser rien du tout.
  let retenue = formules[0];
  for (const f of formules) if (f.total < retenue.total) retenue = f;

  // Supplément nuit courte (v191) : montant fixe, indépendant du choix de
  // formule ci-dessus (constant ajouté à toutes, ne change jamais laquelle
  // est la moins chère). Ligne distincte, jamais fondue dans le tarif/jour.
  const supp = Math.max(0, Number(supplementNuit) || 0);
  const lignesRetenues = retenue.lignes();

  return {
    total: eur(retenue.total + supp),
    lignes: supp > 0
      ? [...lignesRetenues, { label: 'Supplément nuit courte', montant: eur(supp) }]
      : lignesRetenues,
    groupeApplique: retenue.cle !== 'grille',
    formule: retenue.cle,
    supplementNuitApplique: supp > 0,
  };
}

/**
 * Texte des CGV bagages, rempli avec les données RÉELLES du dépôt (décisions
 * Claire, 14/09/2026 — dépôt de droit commun, PAS un dépôt hôtelier au sens de
 * l'article 1953 du Code civil : La Conciergerie n'est pas l'hébergeur, un
 * plafond de responsabilité est donc licite ici, sauf faute lourde/intention-
 * nelle où il devient de toute façon inopposable). Acceptation par simple
 * case à cocher (pas de défilement forcé — la dépose doit rester rapide),
 * contrairement au contrat de location de matériel. Les montants affichés
 * (plafond, jeton perdu, assurance) sont TOUJOURS les valeurs réellement
 * configurées (settings), jamais un exemple figé. AUCUN article sur l'état
 * des bagages à la dépose (retiré — Claire, 14/09/2026 : un état des lieux
 * formel impliquerait un contrôle complet de chaque bagage, impossible en
 * pratique, et exposerait La Conciergerie à devoir prendre en charge l'usure
 * normale de bagages déjà abîmés avant la dépose). PURE.
 * @param {object} p - { nom, nbGrands, nbPetits, depotLabel, retraitLabel,
 *                        plafond, dureeMaxJours, jetonPerduMontant,
 *                        objetsValeur, valeurDeclaree, assuranceSouscrite, assuranceMontant }
 * @returns {Array<{titre:string, texte:string}>}
 */
export function texteContratDepot(p = {}) {
  const nom = String(p.nom || '').trim() || '—';
  const bagagesLabel = `${Math.max(0, Math.floor(p.nbGrands) || 0)} grand(s), ${Math.max(0, Math.floor(p.nbPetits) || 0)} petit(s)`;
  const plafond = eur(p.plafond);
  const jeton = eur(p.jetonPerduMontant);
  const dureeMax = Math.max(1, Math.floor(p.dureeMaxJours) || 30);
  const assurance = eur(p.assuranceMontant);
  return [
    { titre: 'Article 1 — Objet', texte:
      `La Conciergerie (8/10 rue de la porte-neuve, 68000 Colmar) garde temporairement, contre remise d'un jeton ou d'un code, les bagages suivants confiés par ${nom} : ${bagagesLabel}.` },
    { titre: 'Article 2 — Durée', texte:
      `Le dépôt débute le ${p.depotLabel || '—'} ; le retrait est prévu le ${p.retraitLabel || '—'}. Au-delà de ${dureeMax} jours sans retrait, le bagage est considéré comme abandonné ; jusqu'à cette échéance, toutes les journées de garde restent dues au tarif en vigueur, y compris au-delà de la date de retrait prévue.` },
    { titre: 'Article 3 — Objets exclus et déclaration de valeur', texte: p.objetsValeur
      ? `Argent, bijoux, papiers d'identité, appareils électroniques de valeur, objets périssables, liquides et tout objet illégal ou dangereux sont exclus du dépôt. Le client déclare que ses bagages contiennent : ${String(p.valeurDeclaree || '').trim() || '(non précisé)'}.`
      : `Argent, bijoux, papiers d'identité, appareils électroniques de valeur, objets périssables, liquides et tout objet illégal ou dangereux sont exclus du dépôt. Le client déclare ne transporter aucun objet de valeur.` },
    { titre: 'Article 4 — Responsabilité', texte: p.assuranceSouscrite
      ? `En cas de perte, vol ou dommage avéré imputable à La Conciergerie, l'indemnisation correspond à la valeur réelle prouvée du préjudice, plafonnée à ${eur(p.plafondAssurance)}€ par bagage — le client ayant souscrit l'assurance complémentaire (${assurance}€). Ce plafond ne s'applique pas en cas de faute lourde ou intentionnelle de La Conciergerie.`
      : `En cas de perte, vol ou dommage avéré imputable à La Conciergerie, l'indemnisation correspond à la valeur réelle prouvée du préjudice, plafonnée à ${plafond}€ par bagage. Ce plafond ne s'applique pas en cas de faute lourde ou intentionnelle de La Conciergerie.` },
    { titre: 'Article 5 — Jeton ou code de retrait', texte:
      `Le retrait s'effectue exclusivement contre présentation du jeton ou du code remis à la dépose. En cas de perte, une somme de ${jeton}€ est due et une vérification d'identité est effectuée avant restitution.` },
    { titre: 'Article 6 — Droit applicable', texte:
      'Le présent contrat est soumis au droit français. Tout litige sera porté devant les tribunaux compétents.' },
  ];
}

// ─────────────────────────────────────────────────────────────────────
// 2. MATÉRIEL — ESTIMATION (réservation / retrait)
// ─────────────────────────────────────────────────────────────────────

/**
 * Estimation d'une location matériel (un ou plusieurs articles).
 * Calque de calcMulti / calc2 de la démo : tarif jour × quantité × jours,
 * caution = somme des cautions unitaires × quantité.
 *
 * @param {object} p
 * @param {{nom?:string, tarifJour:number, quantite?:number, caution?:number}[]} p.articles
 * @param {number} p.jours  Durée en jours (voir nbJoursDuree).
 * @returns {{ total:number, caution:number,
 *             lignes:{label:string,montant:number}[] }}
 */
export function estimationMateriel({ articles = [], jours = 1 } = {}) {
  const j = Math.max(1, Math.floor(jours) || 1);
  let total = 0, caution = 0, lignes = [];
  for (const a of articles) {
    const q = Math.max(1, Math.floor(a.quantite) || 1);
    const tj = Number(a.tarifJour) || 0;
    const c = Number(a.caution) || 0;
    const sousTotal = tj * q * j;
    total += sousTotal;
    caution += c * q;
    lignes.push({
      label: `${q} × ${a.nom || 'Article'} × ${j}j`,
      montant: eur(sousTotal),
    });
  }
  return { total: eur(total), caution: eur(caution), lignes };
}

// Plafond d'articles DISTINCTS dans un panier de réservation matériel en
// ligne (v183) : la metadata Stripe est limitée en taille par clé, et un
// panier de plusieurs dizaines de lignes n'a de toute façon aucun sens
// métier pour une consigne de quartier. Ajustable si besoin ; le catalogue
// actuel compte 4 articles, large marge.
export const PANIER_MATERIEL_MAX_ARTICLES = 6;

/** Deux intervalles [aDebut,aFin) et [bDebut,bFin) (timestamps ms) se
 *  chevauchent-ils ? Bornes ouvertes : un retour à 18h et un retrait à 18h
 *  ne se chevauchent PAS (l'article change de main au même instant). PURE. */
export function intervallesChevauchent(aDebut, aFin, bDebut, bFin) {
  return aDebut < bFin && bDebut < aFin;
}

/**
 * (v238, SPEC_stock_parc_panier_direct_v1.md) STOCK = PARC TOTAL, OCCUPATION PAR DATES.
 *
 * `quantiteStock` (consigneCatalogue7) est le PARC TOTAL de l'article (modifié par Claire seule, dans Réglages) : il n'est
 * plus jamais décrémenté ni incrémenté automatiquement (l'ancien compteur dérivait dès qu'une location était supprimée ou
 * corrigée). Le disponible est RECALCULÉ à chaque fois depuis les locations, uniquement sur les DATES qui se chevauchent.
 *
 * Quantité d'un article OCCUPÉE sur la fenêtre [debutISO, finISO), tous clients confondus. Ne comptent ni les locations
 * annulées (`annulee`) ni les locations rendues (`retourReel` : le stock est libéré au retour, ou à la suppression d'un test).
 * Occupation d'une entrée :
 *  - réservation non retirée (pas de retraitAt) : [retraitPrevu, retourPrevu) ;
 *  - matériel SORTI et non rendu (retraitAt posé) : [retraitAt, max(retourPrevu, maintenant + 1 ms)) — en retard de retour,
 *    il bloque le moment PRÉSENT seulement (les dates futures restent réservables : à Claire de trouver un remplacement).
 *
 * `nowMs` est INJECTÉ (testable, parité client/serveur). PURE. Miroir STRICT dans create-consigne-checkout et
 * get-consigne-public (SÉCURITÉ : c'est le serveur qui décide, jamais le client).
 * @param {Array} locations - consigneLocations7 (ou un sous-ensemble)
 */
export function quantiteOccupeeArticle(locations, materielId, debutISO, finISO, nowMs = Date.now()) {
  const debut = new Date(debutISO).getTime();
  const fin = new Date(finISO).getTime();
  if (isNaN(debut) || isNaN(fin)) return 0;
  let total = 0;
  for (const e of (locations || [])) {
    if (!e || e.materielId !== materielId || e.retourReel || e.annulee) continue;
    let eDebut, eFin;
    if (e.retraitAt) {
      const sorti = new Date(e.retraitAt).getTime();
      const prevu = new Date(e.retourPrevu).getTime();
      eDebut = isNaN(sorti) ? nowMs : sorti;
      eFin = Math.max(isNaN(prevu) ? nowMs + 1 : prevu, nowMs + 1);
    } else {
      // (v238 T2) Réservation téléphone « en attente de paiement » dont l'ÉCHÉANCE est dépassée : elle ne bloque plus rien. Évalué
      // à la LECTURE (aucune tâche planifiée) — le stock est libre dès la minute où l'échéance passe, pour l'appli et pour la
      // réservation en ligne. Elle reste dans le Registre (« échue »), jamais supprimée.
      if (e.statutPaiement === 'en_attente_paiement' && e.echeancePaiement) {
        const ech = new Date(e.echeancePaiement).getTime();
        if (!isNaN(ech) && ech < nowMs) continue;
      }
      eDebut = new Date(e.retraitPrevu).getTime();
      eFin = new Date(e.retourPrevu).getTime();
    }
    if (isNaN(eDebut) || isNaN(eFin)) continue;
    if (intervallesChevauchent(debut, fin, eDebut, eFin)) total += Math.max(0, Math.floor(Number(e.quantite)) || 0);
  }
  return total;
}

/**
 * (v238) QUI occupe un article sur la fenêtre [debutISO, finISO) : mêmes règles EXACTES que `quantiteOccupeeArticle` (mirror des
 * Edge Functions) mais renvoie le détail — pour expliquer au staff POURQUOI un article est indisponible (« réservée par X du … au … »,
 * « sortie chez X, retour prévu … »). La somme des `quantite` renvoyées égale toujours `quantiteOccupeeArticle` (testé). PURE.
 * @returns {Array<{ id, nom, quantite, sorti:boolean, debutMs:number, finMs:number }>} triées par début
 */
export function occupationsArticle(locations, materielId, debutISO, finISO, nowMs = Date.now()) {
  const debut = new Date(debutISO).getTime();
  const fin = new Date(finISO).getTime();
  if (isNaN(debut) || isNaN(fin)) return [];
  const out = [];
  for (const e of (locations || [])) {
    if (!e || e.materielId !== materielId || e.retourReel || e.annulee) continue;
    let eDebut, eFin;
    if (e.retraitAt) {
      const sorti = new Date(e.retraitAt).getTime();
      const prevu = new Date(e.retourPrevu).getTime();
      eDebut = isNaN(sorti) ? nowMs : sorti;
      eFin = Math.max(isNaN(prevu) ? nowMs + 1 : prevu, nowMs + 1);
    } else {
      // (v238 T2) même règle que quantiteOccupeeArticle : une réservation « en attente de paiement » échue n'occupe plus rien.
      if (e.statutPaiement === 'en_attente_paiement' && e.echeancePaiement) {
        const ech = new Date(e.echeancePaiement).getTime();
        if (!isNaN(ech) && ech < nowMs) continue;
      }
      eDebut = new Date(e.retraitPrevu).getTime();
      eFin = new Date(e.retourPrevu).getTime();
    }
    if (isNaN(eDebut) || isNaN(eFin)) continue;
    if (!intervallesChevauchent(debut, fin, eDebut, eFin)) continue;
    const quantite = Math.max(0, Math.floor(Number(e.quantite)) || 0);
    if (quantite <= 0) continue;
    out.push({ id: e.id, nom: String(e.nom || '').trim() || 'un client', quantite, sorti: !!e.retraitAt, debutMs: eDebut, finMs: eFin });
  }
  return out.sort((a, b) => a.debutMs - b.debutMs);
}

/** Explication courte et lisible d'une indisponibilité, à partir de `occupationsArticle`. PURE.
 *  Ex. « réservée par TEST 7 du 01/10 09:00 au 02/10 18:00 » · « sortie chez Marie, retour prévu 02/10 18:00 » (+ « +1 autre »). */
export function libelleOccupations(occupations, max = 1) {
  const arr = Array.isArray(occupations) ? occupations : [];
  if (arr.length === 0) return '';
  const p2 = (n) => String(n).padStart(2, '0');
  const f = (ms) => { const d = new Date(ms); return `${p2(d.getDate())}/${p2(d.getMonth() + 1)} ${p2(d.getHours())}:${p2(d.getMinutes())}`; };
  const un = (o) => (o.sorti ? `sortie chez ${o.nom}, retour prévu ${f(o.finMs)}` : `réservée par ${o.nom} du ${f(o.debutMs)} au ${f(o.finMs)}`);
  const reste = arr.length - max;
  return arr.slice(0, max).map(un).join(' ; ') + (reste > 0 ? ` (+${reste} autre${reste > 1 ? 's' : ''})` : '');
}

/** Quantité d'un article PHYSIQUEMENT SORTIE maintenant (retirée, non rendue, non annulée), quelles que soient les dates
 *  prévues : sert au contrôle « est-il sur l'étagère ? » et à l'affichage « en boutique maintenant ». PURE. */
export function quantiteSortieArticle(locations, materielId) {
  let total = 0;
  for (const e of (locations || [])) {
    if (!e || e.materielId !== materielId || !e.retraitAt || e.retourReel || e.annulee) continue;
    total += Math.max(0, Math.floor(Number(e.quantite)) || 0);
  }
  return total;
}

/** Quantité disponible d'un article pour une fenêtre donnée : parc total moins l'occupé sur ces dates (jamais négatif). PURE. */
export function quantiteDisponibleArticle(article, locations, debutISO, finISO, nowMs = Date.now()) {
  const parc = Math.max(0, Math.floor(Number(article && article.quantiteStock)) || 0);
  return Math.max(0, parc - quantiteOccupeeArticle(locations, article && article.id, debutISO, finISO, nowMs));
}

/** Quantité d'un article actuellement EN BOUTIQUE (parc − sorti, plancher 0). PURE. */
export function quantiteEnBoutiqueArticle(article, locations) {
  const parc = Math.max(0, Math.floor(Number(article && article.quantiteStock)) || 0);
  return Math.max(0, parc - quantiteSortieArticle(locations, article && article.id));
}

/**
 * Prestataire de caution. Toujours Swikly (v202, décision Claire — Stripe mis
 * de côté pour l'instant ; SEUIL_CAUTION_JOURS n'est plus lu ici mais reste
 * exporté au cas où Stripe reviendrait plus tard sur les cautions courtes).
 * @returns {'swikly'}
 */
export function prestataireCaution() {
  return 'swikly';
}

/**
 * Frais de service caution (Swikly). Le depot d'empreinte Swikly a un cout
 * reel a la pose (~1,99% + 0,25e HT), independamment de tout litige. Ce frais
 * de service, refacture au client, couvre ce cout — nomme « frais de service »
 * et jamais « supplement carte » (surfacturation carte interdite, L112-12 CMF).
 *
 * REGLE (decisions Claire, session 22/08) :
 *  - Desactive tant que settings.fraisCautionActif est faux => 0.
 *  - Gratuite longue duree : si la location dure >= seuil de jours => 0
 *    (le frais devient negligeable face au CA, geste commercial).
 *  - Sinon : montant par tranche de caution ENTAMEE, arrondi a la tranche
 *    superieure. La taille de la tranche est parametrable
 *    (settings.fraisCautionTailleTranche, defaut 50e ; garde => 50 si absent
 *    ou <= 0). Ex. tranche 50e : caution 80e => 2 tranches ; 150e => 3.
 *
 * Fonction PURE. Caution nulle/negative => 0. Toujours arrondi 2 decimales.
 *
 * @param {number} caution   montant de la caution securisee (euros)
 * @param {number} nbJours   duree de la location (jours)
 * @param {object} settings  consigneSettings7 (fraisCautionActif /
 *                           fraisCautionParTranche / fraisCautionTailleTranche /
 *                           fraisCautionSeuilJoursGratuit)
 * @returns {number} frais de service caution TTC, en euros
 */
export function prixFraisCaution(caution, nbJours, settings = {}) {
  const actif = settings.fraisCautionActif === true;
  if (!actif) return 0;
  const c = Number(caution) || 0;
  if (c <= 0) return 0;
  const seuil = Number(settings.fraisCautionSeuilJoursGratuit);
  // Gratuite a partir du seuil (inclus). Seuil <= 0 ou non defini => pas de gratuite.
  if (Number.isFinite(seuil) && seuil > 0 && (Number(nbJours) || 0) >= seuil) return 0;
  const parTranche = Number(settings.fraisCautionParTranche) || 0;
  if (parTranche <= 0) return 0;
  const taille = Number(settings.fraisCautionTailleTranche);
  const t = (Number.isFinite(taille) && taille > 0) ? taille : 50;
  const tranches = Math.ceil(c / t);
  return eur(tranches * parTranche);
}

/**
 * Texte du contrat de location matériel, rempli avec les données RÉELLES de
 * la location (session Claire, 14/09/2026 — remplace l'écran "sommaire" v202
 * par le texte complet des CGV papier, adapté à l'écran). Le frais de
 * service caution affiché est TOUJOURS la valeur réellement calculée
 * (prixFraisCaution, dépend de settings) — jamais un barème d'exemple figé
 * comme dans le document papier d'origine.
 *
 * Retourne un tableau de paragraphes ({ titre, texte }), consommé identiquement
 * par l'écran client (lecture + défilement) et par l'e-mail envoyé à la
 * finalisation (v204) — un seul texte, deux rendus. PURE.
 *
 * @param {object} p - { nom, adresse, telephone, email, articleNom, quantite,
 *                        retraitLabel, retourLabel, prixPrevu, caution,
 *                        fraisCaution }
 * @returns {Array<{titre:string, texte:string}>}
 */
export function texteContratLocation(p = {}) {
  const nom = String(p.nom || '').trim() || '—';
  const adresse = String(p.adresse || '').trim() || '—';
  const telephone = String(p.telephone || '').trim() || '—';
  const email = String(p.email || '').trim() || '—';
  const articleLabel = p.quantite ? `${p.quantite} × ${p.articleNom || 'matériel'}` : (p.articleNom || 'matériel');
  const prix = eur(p.prixPrevu);
  const caution = eur(p.caution);
  const frais = eur(p.fraisCaution);
  return [
    { titre: 'Article 1 — Objet', texte:
      `La Conciergerie (8/10 rue de la porte-neuve, 68000 Colmar) loue à ${nom} (${adresse}, tél. ${telephone}, e-mail ${email}) le matériel suivant : ${articleLabel}.` },
    { titre: 'Article 2 — Durée', texte:
      `La location débute le ${p.retraitLabel || '—'} et se termine le ${p.retourLabel || '—'} (retour prévu).` },
    { titre: 'Article 3 — Prix', texte: caution > 0
      ? `Le montant de la location s'élève à ${prix}€.${frais > 0 ? ` Un dépôt de garantie de ${caution}€ est sécurisé, dont ${frais}€ de frais de service liés à sa sécurisation (montant appliqué par La Conciergerie, jamais un supplément de carte).` : ` Un dépôt de garantie de ${caution}€ est sécurisé, sans frais de service.`}`
      : `Le montant de la location s'élève à ${prix}€. Aucun dépôt de garantie n'est requis pour cet article.` },
    { titre: 'Article 4 — État du matériel et restitution', texte:
      'Le Locataire reconnaît avoir reçu le matériel en bon état de fonctionnement et sans vice apparent. Il s\'engage à le restituer dans le même état à la date convenue, sauf usure normale.' },
    { titre: 'Article 5 — Responsabilité', texte:
      'Le Locataire est responsable de tout dommage, perte ou vol du matériel durant la période de location. La Conciergerie décline toute responsabilité en cas de mauvaise utilisation.' },
    { titre: 'Article 6 — Clause résolutoire', texte:
      'À défaut de restitution du matériel à la date prévue, ou en cas de dégradation constatée, La Conciergerie se réserve le droit de conserver tout ou partie du dépôt de garantie afin de couvrir les frais de remise en état ou de remplacement.' },
    { titre: 'Article 7 — Droit applicable', texte:
      'Le présent contrat est soumis au droit français. Tout litige sera porté devant les tribunaux compétents.' },
  ];
}

/**
 * L'adresse d'une location matériel devient éligible à l'effacement (v204,
 * décision Claire — conservée un délai raisonnable après restitution, pas
 * indéfiniment, pas non plus supprimée AU retour puisque c'est justement le
 * moment où un litige démarre le plus souvent) une fois `retentionMois` mois
 * écoulés depuis le retour RÉEL du matériel (retourReel — jamais retourPrevu,
 * qui n'atteste rien). Pas de retour réel encore posé => jamais éligible.
 * PURE.
 * @param {object} loc         entrée consigneLocations7
 * @param {number} retentionMois
 * @param {string} nowISO      instant de référence (injecté, jamais new Date() direct — testable)
 * @returns {boolean}
 */
export function adresseEligiblePurge(loc, retentionMois, nowISO) {
  if (!loc || !loc.adresse || !loc.retourReel) return false;
  const retour = parseDT(loc.retourReel);
  if (!retour) return false;
  const limite = new Date(retour.getTime());
  limite.setMonth(limite.getMonth() + (Number(retentionMois) || 0));
  const now = parseDT(nowISO) || new Date();
  return now.getTime() >= limite.getTime();
}

/**
 * Purge (effacement du seul champ `adresse`, jamais le reste du dossier —
 * append-only, pas de suppression destructive) des locations dont l'adresse
 * a dépassé le délai de rétention. Déclenchement MANUEL (bouton Registre),
 * jamais un job planifié (aucune tâche cron n'existe dans ce projet). Ne
 * mute pas le tableau d'entrée. PURE.
 * @param {Array} locations
 * @param {number} retentionMois
 * @param {string} nowISO
 * @returns {Array} nouveau tableau (mêmes entrées, sauf adresse effacée sur les éligibles)
 */
export function purgerAdressesExpirees(locations, retentionMois, nowISO) {
  return (Array.isArray(locations) ? locations : []).map((l) =>
    adresseEligiblePurge(l, retentionMois, nowISO)
      ? { ...l, adresse: null, adresseEffaceeAt: nowISO }
      : l
  );
}

// ─────────────────────────────────────────────────────────────────────
// 3. MATÉRIEL — PRIX DÉFINITIF AU RETOUR (poussette « jour dégressif »)
// ─────────────────────────────────────────────────────────────────────
//
// RÈGLE MÉTIER (précisée par Claire en session de code) :
//  - Le prix suit la DEMANDE du client, c.-à-d. le retour PRÉVU au retrait :
//    tarif 24h pile si l'option est enclenchée, ou jours supplémentaires si
//    le client demande à garder la poussette plus longtemps.
//  - Au retour, le prix n'est RÉÉVALUÉ QU'À LA HAUSSE, et seulement si le
//    client est EN RETARD. S'il rend plus tôt que prévu, le prix NE BAISSE
//    PAS : la poussette avait été bloquée pour toute la durée réservée.
//  ⇒ L'instant facturable = le PLUS TARD entre le retour prévu et le retour
//     réel. Le prix définitif est donc toujours ≥ prix prévu (supplément ≥ 0).
//
// Ne concerne que la poussette (mécanisme dégressif). Chaise haute / fauteuil
// / garage = tarif jour plat réglé à la réservation, aucun recalcul au
// retour (SPEC §1.2.3). Lit bébé = nuitées (section 4).

/**
 * Prix poussette sur une FENÊTRE donnée (retrait → borne). Primitive pure,
 * calque exact de calcRetour() de la démo. Sert de brique au prix prévu ET
 * au prix définitif.
 *
 * Formule : tarifJour (jour de base) + (nb de jours calendaires franchis)
 * × tarifJourSupp. Exception "24h pile" : si option24hPile est actif ET que
 * la durée de la fenêtre est < 24h, le PREMIER jour supplémentaire est
 * facturé tarif24hPile au lieu de tarifJourSupp (au-delà, formule linéaire).
 *
 * @returns {{ total:number, dureeHeures:number, joursSupp:number,
 *             detail:{label:string,montant:number}[] }}
 */
export function prixPoussetteFenetre({ retraitAt, borneAt, tarifJour = 0,
                                      tarifJourSupp = 0, tarif24hPile = 0,
                                      option24hPile = false } = {}) {
  const base = Number(tarifJour) || 0;
  const supp = Number(tarifJourSupp) || 0;
  const pile = Number(tarif24hPile) || 0;

  const diffH = dureeHeures(retraitAt, borneAt);
  const dJours = Math.max(0, nbJoursCalendaires(retraitAt, borneAt));

  let detail = [{ label: 'Jour de base', montant: eur(base) }];
  let total = base;
  for (let i = 0; i < dJours; i++) {
    if (i === 0 && option24hPile && diffH < 24) {
      detail.push({ label: 'Jour supp. 1 (tarif 24h pile)', montant: eur(pile) });
      total += pile;
    } else {
      detail.push({ label: `Jour supp. ${i + 1}`, montant: eur(supp) });
      total += supp;
    }
  }
  return { total: eur(total), dureeHeures: diffH, joursSupp: dJours, detail };
}

/**
 * Prix définitif d'une poussette au retour, appliquant la règle « poussette
 * bloquée » : on facture sur le PLUS TARD entre le retour prévu et le retour
 * réel. Un retour anticipé ne réduit jamais le prix ; un retour en retard le
 * réévalue à la hausse.
 *
 * @param {object} p
 * @param {Date|string} p.retraitAt     Retrait réel.
 * @param {Date|string} [p.retourPrevu] Retour demandé au retrait (plancher).
 * @param {Date|string} [p.retourReel]  Retour réel (au clic « Retour »). Si
 *                                       absent : prix = prix prévu.
 * @param {number} p.tarifJour, p.tarifJourSupp, p.tarif24hPile
 * @param {boolean} p.option24hPile
 * @returns {{ prixPrevu:number, prixDefinitif:number, supplement:number,
 *             enRetard:boolean, instantFacturable:string|null,
 *             dureeHeures:number, joursSupp:number,
 *             detail:{label:string,montant:number}[] }}
 */
export function prixRetourPoussette({ retraitAt, retourPrevu, retourReel,
                                     tarifJour = 0, tarifJourSupp = 0,
                                     tarif24hPile = 0, option24hPile = false } = {}) {
  const tarifs = { tarifJour, tarifJourSupp, tarif24hPile, option24hPile };
  const prevu = parseDT(retourPrevu);
  const reel = parseDT(retourReel);

  // Instant facturable = le plus tard entre prévu et réel (plancher = prévu).
  // Sans prévu (walk-in) : on tombe sur le réel. Sans rien : borne = retrait.
  let borne;
  if (prevu && reel) borne = reel > prevu ? reel : prevu;
  else borne = prevu || reel || parseDT(retraitAt);

  const bornePrevu = prevu || parseDT(retraitAt);
  const prevuCalc = prixPoussetteFenetre({ retraitAt, borneAt: bornePrevu, ...tarifs });
  const defCalc = prixPoussetteFenetre({ retraitAt, borneAt: borne, ...tarifs });

  const enRetard = !!(prevu && reel && reel > prevu);
  return {
    prixPrevu: prevuCalc.total,
    prixDefinitif: defCalc.total,
    supplement: eur(Math.max(0, defCalc.total - prevuCalc.total)),
    enRetard,
    instantFacturable: borne ? borne.toISOString() : null,
    dureeHeures: defCalc.dureeHeures,
    joursSupp: defCalc.joursSupp,
    detail: defCalc.detail,
  };
}

/**
 * Supplément générique = prix définitif − montant déjà encaissé, jamais
 * négatif. Pour la poussette, dejaPaye = prixPrevu (encaissé au retrait).
 */
export function supplementRetour(totalDefinitif, dejaPaye = 0) {
  return eur(Math.max(0, (Number(totalDefinitif) || 0) - (Number(dejaPaye) || 0)));
}

// ─────────────────────────────────────────────────────────────────────
// 4. MATÉRIEL — NUITÉES (lit bébé)
// ─────────────────────────────────────────────────────────────────────

/**
 * Nombre de nuits calendaires entre dépôt et reprise (min 1).
 * Logique hôtelière classique : dépôt J, reprise J+2 = 2 nuits (SPEC §1.2.2).
 */
export function nbNuits(depot, reprise) {
  return Math.max(1, nbJoursCalendaires(depot, reprise));
}

/**
 * Prix nuitée sur une FENÊTRE donnée (dépôt → borne). Primitive pure, même
 * patron que prixPoussetteFenetre (v200, décision Claire — tarif dégressif
 * pour la nuit supplémentaire, comme les articles facturés au jour) :
 * 1ère nuit à tarifNuit, nuits suivantes à tarifNuitSupp. tarifNuitSupp non
 * configuré (0/absent) ⇒ repli sur tarifNuit (comportement flat historique
 * préservé tant que Claire n'a pas renseigné le champ dans Réglages —
 * même convention que tarifJourSupp pour la poussette).
 * @returns {{ total:number, nuits:number, detail:{label:string,montant:number}[] }}
 */
export function prixNuitFenetre({ depot, borneAt, tarifNuit = 0, tarifNuitSupp = 0 } = {}) {
  const base = Number(tarifNuit) || 0;
  const supp = Number(tarifNuitSupp) || base;
  const nuits = nbNuits(depot, borneAt);
  const detail = [{ label: 'Nuit de base', montant: eur(base) }];
  let total = base;
  for (let i = 1; i < nuits; i++) {
    detail.push({ label: `Nuit supp. ${i}`, montant: eur(supp) });
    total += supp;
  }
  return { total: eur(total), nuits, detail };
}

/**
 * Prix d'un article facturé à la nuitée (lit bébé). ESTIMATION / prix prévu :
 * 1ère nuit à tarifNuit, nuits suivantes à tarifNuitSupp (v200, dégressif —
 * pas de mécanisme 24h pile, hors périmètre pour cette unité).
 * @returns {{ total:number, nuits:number,
 *             detail:{label:string,montant:number}[] }}
 */
export function prixMaterielNuit({ depot, reprise, tarifNuit = 0, tarifNuitSupp = 0 } = {}) {
  return prixNuitFenetre({ depot, borneAt: reprise, tarifNuit, tarifNuitSupp });
}

/**
 * Prix définitif d'un article à la nuitée (lit bébé) au retour, même règle
 * « bloquée » que la poussette mais comptée en NUITS calendaires (précision
 * de Claire) : on facture sur le PLUS TARD entre reprise prévue et retour
 * réel, en nuits.
 *  - Un retour tardif LE JOUR PRÉVU n'ajoute rien : le jour de retour n'est
 *    jamais une nuit (nuits = jours calendaires franchis, l'heure est ignorée).
 *  - Un retour le lendemain ajoute une nuit.
 *  - Un retour anticipé ne réduit pas (lit réservé, bloqué).
 *
 * @param {object} p
 * @param {Date|string} p.depot         Dépôt réel.
 * @param {Date|string} [p.reprisePrevu] Reprise demandée (plancher).
 * @param {Date|string} [p.retourReel]  Retour réel. Absent → prix = prévu.
 * @param {number} p.tarifNuit
 * @returns {{ prixPrevu:number, prixDefinitif:number, supplement:number,
 *             enRetard:boolean, nuitsPrevu:number, nuitsDefinitif:number,
 *             detail:{label:string,montant:number}[] }}
 */
export function prixRetourNuit({ depot, reprisePrevu, retourReel, tarifNuit = 0, tarifNuitSupp = 0 } = {}) {
  const prevu = parseDT(reprisePrevu);
  const reel = parseDT(retourReel);

  // Borne = le plus tard entre reprise prévue et retour réel (plancher = prévu).
  let borne;
  if (prevu && reel) borne = reel > prevu ? reel : prevu;
  else borne = prevu || reel || parseDT(depot);

  const bornePrevu = prevu || borne;
  const prevuCalc = prixNuitFenetre({ depot, borneAt: bornePrevu, tarifNuit, tarifNuitSupp });
  const defCalc = prixNuitFenetre({ depot, borneAt: borne, tarifNuit, tarifNuitSupp });
  const enRetard = !!(prevu && reel && reel > prevu);
  return {
    prixPrevu: prevuCalc.total,
    prixDefinitif: defCalc.total,
    supplement: eur(Math.max(0, defCalc.total - prevuCalc.total)),
    enRetard,
    nuitsPrevu: prevuCalc.nuits,
    nuitsDefinitif: defCalc.nuits,
    detail: defCalc.detail,
  };
}

// ─────────────────────────────────────────────────────────────────────
// 5. DASHBOARD — AGRÉGATIONS SUR LE REGISTRE caLignes
// ─────────────────────────────────────────────────────────────────────
//
// Fonctions pures consommées par ConsigneStats.jsx (admin-only). Elles lisent
// le registre d'encaissements `consigneCaLignes7_AAAA` : UNE ligne par
// location (CONCEPTION §3, supersede l'idée « 2 lignes » de la spec dashboard).
// Chaque ligne :
//   { at, montant, montantRetrait, montantSupplement, dept:'bagages'|'materiel',
//     article, bienId, factureA, origine, moyen, locationId }
// Le CA est porté par `montant` (= retrait + supplément), horodaté à `at`.
//
// Garde-fous méthodo (SPEC_dashboard_ca_v1 §5) : N exposé partout ; cases à
// faible effectif marquées `faible` (jamais interprétées) ; AUCUNE étiquette
// automatique — l'outil montre, l'humain conclut.

// Seuil de faible effectif par défaut (SPEC §5.2) — configurable côté écran.
export const SEUIL_FAIBLE_DEFAUT = 3;

// ─── (v235) Lignes de CA « secondaires » ─────────────────────────────
// SPEC_modifier_depose_v1.md. Une dépose peut recevoir, après coup, des lignes
// RATTACHÉES à elle (`locationId` = id de la dépose) : complément (sac ajouté),
// prolongation, avoir (retrait anticipé) — et, depuis v209, le frais de jeton
// perdu. Elles s'ajoutent au CA mais ne sont PAS des dépôts : elles ne doivent
// pas gonfler le nombre de dépôts ni fausser le panier moyen.
export const ARTICLE_COMPLEMENT = 'bagages_complement';
export const ARTICLE_PROLONGATION = 'bagages_prolongation';
export const ARTICLE_AVOIR = 'bagages_avoir';
export const ARTICLES_AJUSTEMENT = Object.freeze([ARTICLE_COMPLEMENT, ARTICLE_PROLONGATION, ARTICLE_AVOIR]);

/** Ligne de CA rattachée à une dépose existante (ajustement ou jeton perdu), donc pas un dépôt. PURE. */
export function estLigneSecondaire(l) {
  return !!l && (ARTICLES_AJUSTEMENT.includes(l.article) || l.article === 'jeton_perdu');
}

// Un filtre est neutre si absent / '' / 'tout'.
function filtreNeutre(v) { return v == null || v === '' || v === 'tout'; }

/**
 * Filtre un tableau de lignes CA. Tous les critères sont optionnels.
 * @param {Array} lignes
 * @param {object} [f] { annee, dept, article, bienId }
 */
export function filtrerLignes(lignes = [], f = {}) {
  return (lignes || []).filter((l) => {
    if (!filtreNeutre(f.annee)) {
      const d = parseDT(l.at);
      if (!d || d.getFullYear() !== Number(f.annee)) return false;
    }
    if (!filtreNeutre(f.dept) && l.dept !== f.dept) return false;
    if (!filtreNeutre(f.article) && l.article !== f.article) return false;
    // bienId : filtre explicite. 'aucun' cible les lignes sans bien (vacancier).
    if (!filtreNeutre(f.bienId)) {
      if (f.bienId === 'aucun') { if (l.bienId) return false; }
      else if (l.bienId !== f.bienId) return false;
    }
    return true;
  });
}

/**
 * Indicateurs synthétiques sur un ensemble de lignes déjà filtré.
 * @returns {{ caTotal, nLocations, panierMoyen, caBagages, caMateriel,
 *             nBagages, nMateriel }}
 */
export function kpiConsigne(lignes = []) {
  let caTotal = 0, caBagages = 0, caMateriel = 0, nBagages = 0, nMateriel = 0, n = 0;
  for (const l of lignes) {
    const m = Number(l.montant) || 0;
    caTotal += m;
    const principale = !estLigneSecondaire(l);   // (v235) complément / prolongation / avoir / jeton perdu : du CA, pas un dépôt
    if (principale) n++;
    if (l.dept === 'bagages') { caBagages += m; if (principale) nBagages++; }
    else if (l.dept === 'materiel') { caMateriel += m; nMateriel++; }
  }
  return {
    caTotal: eur(caTotal),
    nLocations: n,
    panierMoyen: n ? eur(caTotal / n) : 0,
    caBagages: eur(caBagages),
    caMateriel: eur(caMateriel),
    nBagages, nMateriel,
  };
}

/**
 * CA agrégé par mois calendaire (1..12). Le caller filtre l'année en amont ;
 * ici on regroupe sur le mois de `at`, tous millésimes confondus.
 * @returns {{ mois:number, ca:number, n:number }[]} longueur 12.
 */
export function caParMois(lignes = []) {
  const acc = Array.from({ length: 12 }, (_, i) => ({ mois: i + 1, ca: 0, n: 0 }));
  for (const l of lignes) {
    const d = parseDT(l.at);
    if (!d) continue;
    const b = acc[d.getMonth()];
    b.ca += Number(l.montant) || 0;
    if (!estLigneSecondaire(l)) b.n += 1;   // (v235) le CA compte tout ; le nombre, seulement les dépôts
  }
  return acc.map((b) => ({ ...b, ca: eur(b.ca) }));
}

/**
 * Numéro de semaine ISO 8601 (semaine commençant lundi, semaine 1 = celle du
 * 1er jeudi). Renvoie l'année ISO (peut différer de l'année civile en bordure)
 * et le numéro de semaine.
 */
export function semaineISO(v) {
  const src = parseDT(v);
  if (!src) return null;
  const d = new Date(src.getFullYear(), src.getMonth(), src.getDate());
  const jour = (d.getDay() + 6) % 7;             // lundi=0 … dimanche=6
  d.setDate(d.getDate() - jour + 3);             // jeudi de la semaine courante
  const isoAnnee = d.getFullYear();
  const premierJeudi = new Date(isoAnnee, 0, 4);
  const jj = (premierJeudi.getDay() + 6) % 7;
  premierJeudi.setDate(premierJeudi.getDate() - jj + 3);
  const semaine = 1 + Math.round((d - premierJeudi) / (7 * 86400000));
  return { isoAnnee, semaine, cle: `${isoAnnee}-W${String(semaine).padStart(2, '0')}` };
}

/**
 * CA agrégé par semaine ISO, trié chronologiquement.
 * @returns {{ cle:string, isoAnnee:number, semaine:number, ca:number, n:number }[]}
 */
export function caParSemaine(lignes = []) {
  const map = new Map();
  for (const l of lignes) {
    const w = semaineISO(l.at);
    if (!w) continue;
    const cur = map.get(w.cle) || { cle: w.cle, isoAnnee: w.isoAnnee, semaine: w.semaine, ca: 0, n: 0 };
    cur.ca += Number(l.montant) || 0;
    cur.n += 1;
    map.set(w.cle, cur);
  }
  return [...map.values()]
    .sort((a, b) => (a.isoAnnee - b.isoAnnee) || (a.semaine - b.semaine))
    .map((b) => ({ ...b, ca: eur(b.ca) }));
}

// Libellés jours (lundi-first, convention FR).
export const JOURS_SEMAINE = Object.freeze(['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim']);

/**
 * Heatmap jour de semaine (lundi=0..dimanche=6) × heure (0..23), intensité = CA.
 * Chaque case porte son N ; `faible` marque les cases 1 ≤ n < seuil (jamais
 * interprétées). Les cases vides (n=0) ne sont pas marquées faibles.
 * @param {Array} lignes
 * @param {object} [opt] { seuil = SEUIL_FAIBLE_DEFAUT }
 * @returns {{ seuil:number, jours:string[], caMax:number,
 *             grille:{ca:number,n:number,faible:boolean}[][] }}  grille[jour][heure]
 */
export function heatmapJourHeure(lignes = [], { seuil = SEUIL_FAIBLE_DEFAUT } = {}) {
  const grille = Array.from({ length: 7 }, () =>
    Array.from({ length: 24 }, () => ({ ca: 0, n: 0, faible: false })));
  for (const l of lignes) {
    const d = parseDT(l.at);
    if (!d) continue;
    const jour = (d.getDay() + 6) % 7;
    const cell = grille[jour][d.getHours()];
    cell.ca += Number(l.montant) || 0;
    cell.n += 1;
  }
  let caMax = 0;
  for (const ligneJour of grille)
    for (const cell of ligneJour) {
      cell.ca = eur(cell.ca);
      cell.faible = cell.n > 0 && cell.n < seuil;
      if (cell.ca > caMax) caMax = cell.ca;
    }
  return { seuil, jours: [...JOURS_SEMAINE], caMax: eur(caMax), grille };
}

/**
 * Lignes CA « orphelines » : sans dépôt/location correspondant dans
 * consigneDepots7 ou consigneLocations7 (locationId absent des deux
 * tableaux). CORRECTIF v199 : peut arriver pour une résa payée en ligne
 * puis supprimée (tests) AVANT le dépôt/retrait physique — voir le
 * correctif dans ConsigneRegistre.supprimerEntree, qui évite désormais ce
 * cas pour les suppressions futures, mais ne rattrape pas les orphelines
 * déjà existantes. Outil de nettoyage réservé à Réglages → Avancé. PURE.
 * @returns {Array} les lignes CA orphelines (jamais l'inverse — on ne
 *          renvoie jamais les lignes à conserver, pour qu'un appelant ne
 *          confonde jamais les deux sens).
 */
export function lignesCaOrphelines(lignesCa, depots, locations) {
  const idsConnus = new Set([
    ...(Array.isArray(depots) ? depots : []).map((d) => d && d.id).filter(Boolean),
    ...(Array.isArray(locations) ? locations : []).map((l) => l && l.id).filter(Boolean),
  ]);
  return (Array.isArray(lignesCa) ? lignesCa : []).filter((l) => l && !idsConnus.has(l.locationId));
}

/**
 * Adaptateur facture Consigne (v201, CONCEPTION_consigne_v1 §... — équivalent de
 * `buildFactureSnapshot` côté propriétaires). Regroupe les lignes CA d'un
 * propriétaire (`factureA===cid`) en sections « Bagages »/« Matériel »,
 * directement consommables par `generateFacturX({ gabarit:'prestations',
 * sections, ... })` de facturxCore.js — aucune duplication du moteur de
 * génération PDF/XML, comme pour les prestations ménage.
 *
 * Source de vérité = les lignes CA déjà écrites au dépôt/retrait/retour
 * (`consigneCaLignes7_AAAA`, `montant` déjà calculé et figé) — jamais un
 * recalcul de prix ici, cet adaptateur ne fait qu'agréger et mettre en forme.
 *
 * `biensById`/`catalogueById` : Maps (id -> objet), construites par l'appelant
 * (`indexById` de ConsigneRegistre.jsx) — injectées plutôt qu'importées, pour
 * que ce module reste sans dépendance React/écran (cohérent avec le reste de
 * consigneCore.js).
 * `dejaFactureIds` : ensemble de `locationId` déjà inclus dans une facture
 * Consigne émise/payée — anti-double-facturation, même principe que
 * `tachesFacturees` côté prestations ménage.
 *
 * @returns {{ sections: Array, idsUtilises: string[], total: number }}
 */
export function buildLignesConsigne({ lignesCa, cid, biensById, catalogueById, dejaFactureIds } = {}) {
  const dejaSet = dejaFactureIds instanceof Set ? dejaFactureIds : new Set(dejaFactureIds || []);
  const bMap = biensById instanceof Map ? biensById : new Map();
  const cMap = catalogueById instanceof Map ? catalogueById : new Map();

  const lignesCli = (Array.isArray(lignesCa) ? lignesCa : [])
    .filter((l) => l && cid && l.factureA === cid && l.locationId && !dejaSet.has(l.locationId));

  const bagages = [];
  const materiel = [];
  const idsUtilises = [];
  for (const l of lignesCli) {
    const montant = eur(l.montant);
    if (montant === 0) continue; // rien à facturer sur une ligne à 0€ (ex. gratuité ponctuelle)
    idsUtilises.push(l.locationId);
    const bien = l.bienId ? bMap.get(l.bienId) : null;
    const nomBien = bien ? (bien.name || String(l.bienId)) : (l.bienId ? String(l.bienId) : '—');
    const d = parseDT(l.at);
    const dt = d ? `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}` : '';
    if (l.dept === 'materiel') {
      const art = l.article ? cMap.get(l.article) : null;
      const nomArt = art ? (art.nom || String(l.article)) : String(l.article || 'Matériel');
      materiel.push({ d: `${nomArt} - ${nomBien}`, dt, q: 1, pu: montant });
    } else if (l.article === ARTICLE_AVOIR) {
      // Avoir : QUANTITÉ −1 et prix unitaire POSITIF (EN 16931 : un prix net ne peut pas être négatif).
      bagages.push({ d: `Avoir — dépôt bagages - ${nomBien}`, dt, q: -1, pu: Math.abs(montant) });
    } else if (l.article === ARTICLE_COMPLEMENT) {
      bagages.push({ d: `Complément dépôt bagages - ${nomBien}`, dt, q: 1, pu: montant });
    } else if (l.article === ARTICLE_PROLONGATION) {
      bagages.push({ d: `Prolongation dépôt bagages - ${nomBien}`, dt, q: 1, pu: montant });
    } else {
      bagages.push({ d: `Dépôt bagages - ${nomBien}`, dt, q: 1, pu: montant });
    }
  }

  const sections = [];
  if (bagages.length) sections.push({ lbl: 'Bagages', lignes: bagages });
  if (materiel.length) sections.push({ lbl: 'Matériel', lignes: materiel });
  const total = eur([...bagages, ...materiel].reduce((a, l) => a + l.pu * l.q, 0));
  return { sections, idsUtilises, total };
}

// ─────────────────────────────────────────────────────────────────────
// 6. DÉFAUTS DE CONFIGURATION (settings boutique + catalogue de démarrage)
// ─────────────────────────────────────────────────────────────────────
//
// Consommés par le data-layer (useSt dans App.jsx) et l'écran Réglages.
// ⚠️ Placés EN FIN de fichier : ils référencent TARIFS_CONSIGNE_DEFAUT (haut)
// et SEUIL_FAIBLE_DEFAUT (§5), tous deux définis PLUS HAUT — évite la zone
// morte temporelle (les const de module s'évaluent de haut en bas, TDZ non
// rattrapée par ESLint ni par le build, seulement au runtime).

// ─── (v235) Commissions partenaires — Bounce / Radical Storage ───────
// SPEC_ticket_libre_commissions_v1.md §3. Le client de ces plateformes paie le
// PARTENAIRE, pas la boutique : aucun tarif à l'écran, aucun encaissement. Le
// revenu que la boutique en retire est une COMMISSION par bagage (grand/petit
// distincts, indépendante de la durée), réglée dans Réglages. Elle devient le
// `prix` du dépôt donc le `montant` de la ligne de CA : les statistiques
// comptent le revenu réel. Vocabulaire « commission » autorisé dans le module
// Consigne (décision Claire 20/09/2026 — la consigne n'a rien à voir avec la
// carte G).
export const ORIGINES_PARTENAIRES = Object.freeze(['bounce', 'radical']);
export const LIBELLES_PARTENAIRES = Object.freeze({ bounce: 'Bounce', radical: 'Radical Storage' });

/** L'origine est-elle une plateforme partenaire (paiement hors comptoir) ? PURE. */
export function estOriginePartenaire(origine) {
  return ORIGINES_PARTENAIRES.includes(origine);
}

/**
 * Commission due à la boutique pour un dépôt partenaire :
 * nb grands × commission grand + nb petits × commission petit (par bagage,
 * indépendant de la durée). 0 pour toute autre origine ou si non réglée. PURE.
 */
export function commissionPartenaire(settings, origine, nbGrands, nbPetits) {
  if (!estOriginePartenaire(origine)) return 0;
  const c = (settings && settings.commissionPartenaires && settings.commissionPartenaires[origine]) || {};
  const g = Math.max(0, Number(c.grand) || 0);
  const p = Math.max(0, Number(c.petit) || 0);
  const nG = Math.max(0, Math.floor(Number(nbGrands) || 0));
  const nP = Math.max(0, Math.floor(Number(nbPetits) || 0));
  return eur(nG * g + nP * p);
}

export const CONSIGNE_SETTINGS_DEFAUT = Object.freeze({
  moduleActif: false,               // rodage en parallèle des 4 Jotform (décision Claire)
  horaires: {                       // heures boutique — configurables, jamais figées
    lun: { ouv: '09:00', ferm: '18:00' },
    mar: { ouv: '09:00', ferm: '18:00' },
    mer: { ouv: '09:00', ferm: '18:00' },
    jeu: { ouv: '09:00', ferm: '18:00' },
    ven: { ouv: '09:00', ferm: '18:00' },
    sam: { ouv: '09:00', ferm: '18:00' },
    dim: { ouv: '', ferm: '' },     // fermé par défaut
  },
  fermetures: [],                   // jours/plages/hebdo (portage closeDay/Range/Weekly démo)
  whatsapp: '',                     // retrait hors horaires (Direct/Réservé)
  tarifs: { ...TARIFS_CONSIGNE_DEFAUT },   // grand/petit/preferentiel/groupe/groupeSeuil
  // (v235) commission par bagage (€) — 0 tant que non renseignée (avertissement dans Réglages)
  commissionPartenaires: { bounce: { grand: 0, petit: 0 }, radical: { grand: 0, petit: 0 } },
  supplement24hActif: false,        // BAGAGES > 24h — OFF défaut (arbitrage Franck)
  supplement24hMontant: 2,          // distinct du « 24h pile » poussette (catalogue)
  supplement24hSeuilHeure: 11,      // heure limite de retrait le lendemain (v191, décision Claire)
  fraisCautionActif: false,             // frais de service caution (Swikly) — OFF défaut
  fraisCautionParTranche: 2,            // e par tranche de caution entamee (arrondi sup.)
  fraisCautionTailleTranche: 50,        // taille d'une tranche de caution (e) — parametrable
  fraisCautionSeuilJoursGratuit: 5,     // location >= ce nb de jours => frais offerts
  adresseRetentionMois: 24,             // (v204) rétention adresse locataire matériel, purge manuelle
  delaiPaiementTelHeures: 72,           // (v238 T2) délai de paiement d'une réservation téléphone « en attente » (échéance, puis stock libéré)
  coordonneesBancaires: { titulaire: '', iban: '', bic: '' },   // (v238 T2) RIB des mails de paiement — JAMAIS envoyé à la tablette (REGLAGES_RETIRES)
  bagageResponsabilitePlafond: 150,     // (v209) plafond CGV bagages, € par bagage, hors faute lourde
  bagageAssuranceMontant: 10,           // (v209) supplément assurance complémentaire optionnelle, €
  bagageAssurancePlafond: 500,          // (v209) plafond porté à ce montant si assurance souscrite
  bagageDureeMaxGardeJours: 30,         // (v209) au-delà -> bagage considéré abandonné (CGV)
  jetonPerduMontant: 5,                 // (v209) frais si le jeton n'est pas rendu au retrait
  emplacementsMax: 80,              // grille 1-80 + jeton physique
  // (v233) Numéros déportés — haute saison
  zones: [],                         // { id: 'z-xxx', nom: 'Toilettes', actif: true }
  numerosDeportesDebut: 101,         // premier numéro de la plage déportée (> emplacementsMax)
  imprimanteIp: '',                  // IP locale de l'Epson TM-T20III (ePOS-Print)
  imprimanteDeviceId: 'local_printer',
  // (v233) Ticket — contenu conditionnel selon l'origine
  ticketWhatsapp: '',                // numéro WhatsApp affiché sur le ticket (clients directs)
  ticketHoraires: '',                // ex. "Ouvert : 9h – 19h"
  ticketOriginesWhatsapp: ['direct', 'reserve'],  // origines qui reçoivent le bloc WhatsApp
  personnel: [                      // alimente le menu « Identifiant du staff » au comptoir (qui prend le dépôt)
    { id: 'p-claire', nom: 'Claire', role: 'Gérant' },
    { id: 'p-franck', nom: 'Franck', role: 'Gérant' },
  ],
  serieFacture: 'FC-CO',            // préfixe série facturation consigne (validé Claire)
  seuilFaibleDashboard: SEUIL_FAIBLE_DEFAUT,   // 3 — dashboard CA
});

// Catalogue de démarrage : 1 unité/article (décision actée). recalculRetour :
// true = prix réévalué au retour réel ('jour' → prixRetourPoussette, 'nuit' →
// prixRetourNuit) ; false = réglé à la résa. exception24hPile OFF partout au départ.
export const CONSIGNE_CATALOGUE_DEFAUT = Object.freeze([
  { id: 'poussette',        nom: 'Poussette canne',  description: '', uniteFacturation: 'jour', tarifJour: 8,  tarifJourSupp: 6,  tarif24hPile: 2, exception24hPile: false, recalculRetour: true,  caution: 80,  quantiteStock: 1, actif: true },
  { id: 'lit-bebe',         nom: 'Lit bébé',         description: '', uniteFacturation: 'nuit', tarifJour: 7,  tarifJourSupp: 0,  tarif24hPile: 0, exception24hPile: false, recalculRetour: true,  caution: 60,  quantiteStock: 1, actif: true, tarifNuitSupp: 5 },
  { id: 'chaise-haute',     nom: 'Chaise haute',     description: '', uniteFacturation: 'jour', tarifJour: 8,  tarifJourSupp: 8,  tarif24hPile: 0, exception24hPile: false, recalculRetour: false, caution: 100, quantiteStock: 1, actif: true },
  { id: 'fauteuil-roulant', nom: 'Fauteuil roulant', description: '', uniteFacturation: 'jour', tarifJour: 15, tarifJourSupp: 15, tarif24hPile: 0, exception24hPile: false, recalculRetour: false, caution: 150, quantiteStock: 1, actif: true },
  { id: 'garage',           nom: 'Garage / parking', description: '', uniteFacturation: 'jour', tarifJour: 14, tarifJourSupp: 14, tarif24hPile: 0, exception24hPile: false, recalculRetour: false, caution: 50,  quantiteStock: 1, actif: true },
]);

// ═══════════════════════════════════════════════════════════════════
// (v236) FERMETURES EXCEPTIONNELLES — réservation en ligne
// ═══════════════════════════════════════════════════════════════════
// Bug signalé par Claire le 24/09/2026 : après avoir fermé la boutique du
// 25 au 28/09 dans Réglages, la réservation en ligne restait possible sur
// ces dates. Cause : `fermetures` était écrit par l'écran Réglages mais LU
// PAR PERSONNE (ni la page publique, ni get-consigne-public, ni
// create-consigne-checkout).
//
// DÉCISIONS DE CLAIRE (24/09/2026) : (A1) on refuse la réservation dont la
// date de DÉPÔT ou la date de RETRAIT tombe un jour fermé (les jours
// intermédiaires ne comptent pas : déposer le 24 et reprendre le 29 reste
// possible) ; même règle pour le matériel (retrait / retour) ; (B) le
// COMPTOIR n'est PAS bloqué : on peut encore enregistrer une consigne, on ne
// peut plus RÉSERVER sur internet ; (C) refus franc, côté page ET côté
// serveur (la page seule se contourne).
//
// ⚠️ MIROIR STRICT : `dateBoutique`, `dateFermee`, `reservationFermee` sont
// recopiés VERBATIM dans supabase/functions/create-consigne-checkout/index.ts
// (décision faisant foi) ; `fermeturesPubliques` vit dans get-consigne-public.
// Parité vérifiée par tests/fermetures_reservation.test.mjs. Les Edge
// Functions ne se redéploient PAS au push : recopie manuelle.

const FUSEAU_BOUTIQUE = 'Europe/Paris';

/**
 * Date calendaire (AAAA-MM-JJ) d'un instant, à l'heure de la BOUTIQUE
 * (Europe/Paris) et non celle du navigateur ou du serveur (UTC) : à 00h30
 * heure de Colmar, l'ISO UTC est encore la veille. null si non interprétable.
 */
export function dateBoutique(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: FUSEAU_BOUTIQUE, year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(d);
  const get = (t) => {
    const p = parts.find((x) => x.type === t);
    return p ? p.value : undefined;
  };
  const y = get('year'), m = get('month'), j = get('day');
  return y && m && j ? `${y}-${m}-${j}` : null;
}

/**
 * Vrai si la date (AAAA-MM-JJ) tombe dans une fermeture exceptionnelle.
 * Forme des entrées (écran Réglages) : { type:'jour', date } ·
 * { type:'plage', du, au } (bornes INCLUSES) ·
 * { type:'hebdo', jours:[0..6 (0 = dimanche)], du|null, au|null } (bornes
 * incluses, absentes = sans limite). Entrée mal formée = ignorée (une
 * fermeture qu'on ne sait pas lire ne doit jamais faire planter la résa).
 */
export function dateFermee(dateStr, fermetures) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(dateStr || '')) || !Array.isArray(fermetures)) return false;
  const [y, m, j] = dateStr.split('-').map(Number);
  const dow = new Date(Date.UTC(y, m - 1, j)).getUTCDay();
  for (const c of fermetures) {
    if (!c || typeof c !== 'object') continue;
    if (c.type === 'jour') {
      if (c.date === dateStr) return true;
    } else if (c.type === 'plage') {
      if (c.du && c.au && c.du <= dateStr && dateStr <= c.au) return true;
    } else if (c.type === 'hebdo') {
      if (!Array.isArray(c.jours) || !c.jours.map(Number).includes(dow)) continue;
      if (c.du && dateStr < c.du) continue;
      if (c.au && dateStr > c.au) continue;
      return true;
    }
  }
  return false;
}

/**
 * Une réservation en ligne (dépôt/retrait ISO) tombe-t-elle un jour fermé ?
 * Seules les deux dates saisies comptent (décision A1). Renvoie
 * { ferme:boolean, dates:[AAAA-MM-JJ fermées, sans doublon] }.
 */
export function reservationFermee(depotISO, retraitISO, fermetures) {
  const dates = [];
  for (const iso of [depotISO, retraitISO]) {
    const jour = dateBoutique(iso);
    if (jour && dateFermee(jour, fermetures) && !dates.includes(jour)) dates.push(jour);
  }
  return { ferme: dates.length > 0, dates };
}
